import dayjs from 'dayjs';
import { APIClient } from '@innerhive-internal/innerhive-api-client';
import { State } from '@meraki-internal/state';
import { ICareCheckin, ICheckin, ICheckinType, IFeeling, INarrowedCheckin, IPersonalCheckin } from './ICheckin';
import { INote } from '../../notes/generic-notes/INote';
import { CheckinNoteSavingQueue } from './CheckinNoteSavingQueue';
import { CareMapState } from '../../caremap/CareMapState';
import { ICareMap } from '../../caremap/ICareMap';

export const NEW_NOTE_ID = 'NEW';

const ISO_FORMAT_WITH_TZ_OFFSET = 'YYYY-MM-DDTHH:mm:ss.SSSZ';

export type ISO8601Month = string; // YYYY-MM

type ICheckinState = {
    selectedMonth: ISO8601Month;

    checkinType: ICheckinType;

    // these are loaded at app start
    personalNotes: INote[];

    // these are loaded when care map is set/changes
    careMap?: ICareMap;
    careNotes?: INote[];

    // these are set when editing
    editingDate?: string;
    personalCheckin?: IPersonalCheckin;
    careCheckin?: ICareCheckin;

    trackingFrom?: string;
};

export class CheckinState extends State<ICheckinState> {

    static inject = () => [APIClient, CheckinNoteSavingQueue, CareMapState];
    constructor(private apiClient: APIClient, private savingQueue: CheckinNoteSavingQueue, private careMapState: CareMapState) {
        super({
            selectedMonth: dayjs().format('YYYY-MM'),
            checkinType: 'personal',
            personalNotes: [],
        });
    }

    private getNotes = (checkinType: ICheckinType) => {
        const notes = this.state[`${checkinType}Notes`];
        return notes;
    };

    private updateNotes = (checkinType: ICheckinType, updater: (existing: INote[]) => INote[]) => {
        const notes = this.getNotes(checkinType)!;
        this.setState({
            [`${checkinType}Notes`]: updater(notes)
        });
    };

    private getNote = (checkinType: ICheckinType, noteId: string) => {
        const notes = this.getNotes(checkinType)!;
        const note = notes.find(n => n.noteId === noteId);
        if (!note) {
            throw new Error(`Could not find ${this.state.checkinType} note with id ${noteId}`);
        }
        return note;
    };

    private addNote = (checkinType: ICheckinType, newNote: INote) => {
        this.updateNotes(checkinType, notes => [...notes, newNote]);
    };

    private updateNote = (checkinType: ICheckinType, updated: INote) => {
        this.updateNotes(checkinType, notes => notes.map(note => note.noteId === updated.noteId ? updated : note));
    };

    private buildCheckinFromNote = <T extends ICheckinType>(checkinType: T, note: any): INarrowedCheckin<T> => {
        const baseCheckin: ICheckin = {
            checkinType,

            // default to createdAt, but should get overwritten by timestamp from
            // otherAttributes, which is in the timezone where the checkin was created
            timestamp: note.createdAt,

            noteId: note.noteId,
            note: note.html,
            attachments: note.attachments,
            tags: note.tags,
        };

        return {
            ...baseCheckin,
            ...note.otherAttributes
        };
    };

    private buildNewCheckin = <T extends ICheckinType>(date: string, checkinType: T): INarrowedCheckin<T> => {
        // if no saved checkin for this date, create a new one...
        let day: dayjs.Dayjs;
        if (dayjs(date).isToday()) {
            // if today, set timestamp to current time
            day = dayjs();
        } else {
            // if a day in the past, set timestamp to midday
            day = dayjs(date).set('h', 12).startOf('h');
        }

        // timestamp is in user's current local time zone
        // (so ends with offset like "+04:00" instead of "Z")
        const timestamp = day.format(ISO_FORMAT_WITH_TZ_OFFSET);

        const checkin: ICheckin = {
            checkinType,
            noteId: NEW_NOTE_ID,
            timestamp,
            note: '',
            attachments: []
        };

        return checkin as any;
    };

    getSelectedMonth = () => {
        return this.state.selectedMonth;
    };

    selectMonth = (month: ISO8601Month) => {
        this.setState({
            selectedMonth: month
        });
    };

    load = async () => {
        await this.loadPersonal();

        this.careMapState.subscribe(async () => {
            const careMap = this.careMapState.getMostRecentCaremap();
            if (careMap !== this.state.careMap) {
                await this.loadCare(careMap);
            }
        });
    };

    private loadPersonal = async () => {
        const entry = await this.apiClient.entry();
        const notes = await this.loadFromLink(entry.links.checkins);
        this.setState({ personalNotes: notes });
    };

    private loadCare = async (careMap?: ICareMap) => {
        this.setState({ careMap, careNotes: undefined, careCheckin: undefined });

        if (careMap) {
            const link = this.state.careMap?.links.checkins;
            if (link) {
                const notes = await this.loadFromLink(link);
                this.setState({ careNotes: notes });

                if (this.isEditing()) {
                    const date = this.state.editingDate!;
                    const careCheckin = this.getCheckinForDay(date, 'care');
                    this.setState({ careCheckin });
                }
            }
        }
    };

    private loadFromLink = async (checkinsLink: any) => {
        if (!checkinsLink) {
            // not supported for this user
            return [];
        }

        const notes: INote[] = await this.apiClient.get(checkinsLink);

        // sort by timestamp (which is in timezone where checkin was created)
        notes.sort((c1, c2) => {
            const ts1 = this.buildCheckinFromNote('personal', c1).timestamp;
            const ts2 = this.buildCheckinFromNote('personal', c2).timestamp;
            return ts1.localeCompare(ts2);
        });

        return notes;
    };

    getPersonalCheckins = () => {
        // personal checkins are loaded on app start, so we can assert they exist
        return this.getCheckins('personal')!;
    };

    getCareCheckins = () => {
        return this.getCheckins('care')!;
    };

    hasData = (c: ICheckin) => {
        if (c.checkinType === 'personal') {
            return (c.feeling || c.emotions?.length || c.lifeEvents?.length || c.energy || c.personalTime || c.note || c.attachments?.length || c.tags?.length);
        }
        if (c.checkinType === 'care') {
            // IH-6574: Excluded recipient wellness fields until feature is released
            // return (c.feeling || c.emotions?.length || c.lifeEvents?.length || c.energy ||
            //         c.careEvents?.length || c.totalCareHours || c.milesDriven || c.note || c.attachments?.length);
            return (c.careEvents?.length || c.totalCareHours || c.milesDriven || c.note || c.attachments?.length || c.tags?.length);
        }
    };

    private getCheckins = <T extends ICheckinType>(checkinType: T) => {
        const notes = this.getNotes(checkinType);
        if (!notes) {
            return undefined; // not loaded
        }
        return notes.map(n => this.buildCheckinFromNote(checkinType, n));
    };

    // return checkin date in format "YYYY-MM-DD"
    getCheckinDate = (checkin: ICheckin) => {
        // NEVER use dayjs here or you can get the wrong answer!!

        // For example, if I go to LA and create a checkin at 10pm there,
        // its timestamp will be "2024-06-30T22:00:00.000Z-08:00",
        // if I then return to NY, dayjs(timestamp).format('YYYY-MM-DD')
        // will return "2024-07-01" which is not what we want.

        // We ALWAYS just want to look at the first 10 characters.
        return checkin.timestamp.substring(0, 10);
    };

    // get latest checkin logged for this day
    getFinalCheckin = <T extends ICheckinType>({ date, checkinType }: { date: string, checkinType: T }) => {
        const checkins = this.getCheckins(checkinType);
        if (!checkins) {
            throw new Error(`${checkinType} checkins not loaded`);
        }

        // intentionally using YYYY-MM-DD so we filter checkins
        // based on the date they were created in the local time zone
        const timestampPrefix = dayjs(date).format('YYYY-MM-DD');

        for (let i = checkins.length; i > 0; i--) {
            const checkin = checkins[i - 1];
            if (checkin.timestamp.startsWith(timestampPrefix)) {
                return checkin;
            }
        }

        return undefined;
    };

    private getCheckinForDay = <T extends ICheckinType>(date: string, checkinType: T) => {
        if (!this.getCheckins(checkinType)) {
            return undefined; // not loaded
        }

        const checkin = this.getFinalCheckin({ date, checkinType });

        if (checkin) {
            return checkin;
        } else {
            return this.buildNewCheckin(date, checkinType);
        }
    };

    getPersonalCheckinForToday = () => {
        return this.getCheckinForDay(dayjs().toISOString(), 'personal')!;
    };

    savePersonalCheckin = async (checkin: IPersonalCheckin) => {
        const entry = await this.apiClient.entry();
        if (!entry.links.checkins) {
            throw new Error('checkins not supported for this user');
        }
        this.saveToLink(checkin, entry.links.checkins);
    };

    saveCareCheckin = async (checkin: ICareCheckin) => {
        const checkinsLink = this.state.careMap?.links.checkins;
        if (!checkinsLink) {
            throw new Error('checkins not supported for this care map');
        }
        this.saveToLink(checkin, checkinsLink);
    };

    private saveToLink = (checkin: ICheckin, checkinsLink: any) => {
        // tags is a top-level note field (like html/attachments), so pull it out of the
        // destructure to keep it from being saved into otherAttributes
        const { checkinType, noteId: checkinNoteId, note = '', attachments = [], tags = [], ...otherAttributes } = checkin;
        const stateKey = `${checkinType}Checkin`;

        // optimistically update local state before saving
        this.setState({ [stateKey]: checkin });
        if (checkinNoteId !== NEW_NOTE_ID) {
            this.updateNote(checkinType, { ...this.getNote(checkinType, checkinNoteId), otherAttributes });
        }

        this.savingQueue.enqueue({

            noteId: checkinNoteId,

            action: async () => {
                let noteId = checkinNoteId;

                // check if we have a saved note for this day, in case this was a second
                // "NEW" request submitted while the previous one was still saving
                if (noteId === NEW_NOTE_ID) {
                    const timestampDate = this.getCheckinDate(checkin);
                    ({ noteId } = this.getCheckinForDay(timestampDate, checkinType)!);
                }

                if (noteId === NEW_NOTE_ID) {
                    const noteToSave = { title: '', html: note, attachments, tags, otherAttributes };
                    const savedNote = await this.apiClient.post(checkinsLink, noteToSave);
                    this.addNote(checkinType, savedNote);
                } else {
                    const noteToSave = { ...this.getNote(checkinType, noteId), html: note, attachments, tags, otherAttributes };
                    const savedNote = await this.apiClient.put(noteToSave);
                    this.updateNote(checkinType, savedNote);
                }
            }
        });
    };

    private maybeSavePersonalCheckin = async (checkin: IPersonalCheckin) => {
        const entry = await this.apiClient.entry();
        if (!entry.links.checkins) {
            console.log('saving checkins not supported for this user');
        } else {
            this.saveToLink(checkin, entry.links.checkins);
        }
    };

    startEditing = ({ date, feeling, trackingFrom } : { date?: string, feeling?: IFeeling, trackingFrom: string }) => {
        // default to today if not specified
        date = date || dayjs().toISOString();

        let checkinType = this.state.checkinType;

        const personalCheckin = this.getCheckinForDay(date, 'personal');
        const careCheckin = this.getCheckinForDay(date, 'care');

        if (personalCheckin && feeling) {
            checkinType = 'personal';
            personalCheckin.feeling = feeling;
            this.maybeSavePersonalCheckin(personalCheckin);
        }

        this.setState({ checkinType, editingDate: date, personalCheckin, careCheckin, trackingFrom });
    };

    stopEditing = () => {
        // leave personalCheckin/careCheckin set so modal doesn't change while animating closed
        this.setState({ editingDate: undefined });
    };

    isEditing = () => {
        return Boolean(this.state.editingDate);
    };

    getEditingCheckin = <T extends ICheckinType>(checkinType: T): INarrowedCheckin<T> | undefined => {
        return this.state[`${checkinType}Checkin`] as any;
    };
};
